import React from 'react';
import { Mail, Twitter, Facebook, BarChart2, BookOpen, Clock, Code, Database, Globe, Heart, LightbulbIcon, Shield, Target, Users, MessageCircle, Instagram } from 'lucide-react';
import { useTheme } from '../components/ThemeContext';
export function About() {
  const {
    theme
  } = useTheme();
  return (
    <div className="flex flex-col min-h-screen bg-theme-primary">
      {/* Hero Section */}
      <section className={`relative py-20 ${theme === 'dark' ? 'bg-gradient-to-br from-blue-900 to-gray-900 text-theme-primary' : 'bg-gradient-to-br from-blue-100 to-gray-100 text-gray-900'}`}>
        <div className="absolute inset-0 overflow-hidden">
          <div className={`absolute top-0 right-0 w-1/3 h-1/3 rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2 ${theme === 'dark' ? 'bg-blue-600/10' : 'bg-blue-400/20'}`}></div>
          <div className={`absolute bottom-0 left-0 w-1/2 h-1/2 rounded-full blur-3xl transform translate-x-1/4 translate-y-1/4 ${theme === 'dark' ? 'bg-green-600/10' : 'bg-green-400/20'}`}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Our Mission is Your Financial Success
            </h1>
            <p className="text-xl opacity-80 mb-8">
              Stock Fortress provides professional-grade valuation tools to help
              investors make smarter, data-driven decisions in an increasingly
              complex market.
            </p>
          </div>
        </div>
      </section>
      {/* Our Story Section */}
      <section className="py-16 md:py-24 bg-theme-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-theme-primary">
              Our Story
            </h2>
            <div className="prose prose-lg max-w-none text-theme-secondary">
              <p>
                Stock Fortress began in 2023 as a founder-led project. Frustrated
                that institutional-grade valuation tools weren’t accessible to
                everyday investors, I rolled up my sleeves and partnered with a
                small group of developers to build something better.
              </p>
              <p className="mt-4">
                Over the next year, we prototyped relentlessly with real market
                data—bringing rigorous financial models into a workflow that
                individual investors can actually use. The goal was simple: make
                professional-quality analysis intuitive and affordable.
              </p>
              <p className="mt-4">
                By mid‑2024, we invited a handful of professional investors to
                pressure‑test the platform. Their feedback shaped our DCF engine,
                comparative valuation and scenario modeling, and guided the
                addition of machine‑learning powered forecasts and smarter
                screeners.
              </p>
              <p className="mt-4">
                Today, we’re opening Stock Fortress to a wider audience—keeping
                the professional bar high while expanding access to investors of
                all levels. This is just the beginning, and we’re excited to grow
                with you.
              </p>
            </div>
            {/* Timeline */}
            <div className="mt-16">
              <h3 className="text-2xl font-bold mb-6 text-theme-primary">
                Our Journey
              </h3>
              <div className="space-y-8">
                {[{
                year: '2023',
                title: 'Idea & First Build',
                description: 'Founder-led start; core concept shaped and first prototypes built with a small dev team.'
              }, {
                year: '2024',
                title: 'Prototype Iteration',
                description: 'Rapid iterations with real market data; refined DCF, comparative valuation, and scenarios.'
              }, {
                year: '2024',
                title: 'Private Beta',
                description: 'Invited professional investors to pressure-test workflows and validate results.'
              }, {
                year: '2025',
                title: 'Feature Expansion',
                description: 'Added ML-powered forecasts, smarter screeners, and portfolio tools guided by practitioner feedback.'
              }, {
                year: '2025',
                title: 'Opening Access',
                description: 'Expanding from professionals to all investors while keeping a high professional bar.'
              }].map((milestone, index) => (
                <div key={index} className="flex">
                  <div className="flex flex-col items-center mr-4">
                    <div className="flex items-center justify-center p-2 rounded-full active-bg text-theme-primary">
                      <Clock className="w-5 h-5" />
                    </div>
                    {index < 4 && (
                      <div className="h-full w-0.5 bg-blue-600/30 mt-2"></div>
                    )}
                  </div>
                  <div className="pt-1.5">
                    <p className="text-theme-primary font-semibold">
                      {milestone.year}
                    </p>
                    <h4 className="text-lg font-bold text-theme-primary mt-1">
                      {milestone.title}
                    </h4>
                    <p className="text-theme-secondary mt-1">
                      {milestone.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
      </section>
      {/* Our Values */}
      <section className="py-16 bg-theme-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-theme-primary">
              Our Values
            </h2>
            <p className="text-xl text-theme-secondary mt-4 max-w-3xl mx-auto">
              These core principles guide everything we do at Stock Fortress
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[{
              icon: <Shield className="w-8 h-8 active-text" />,
              title: 'Integrity',
              description:
                "We believe in transparent, honest analysis that serves the investor's best interests, not hidden agendas.",
            },
            {
              icon: <Database className="w-8 h-8 active-text" />,
              title: 'Data-Driven',
              description:
                'Our analysis is based on comprehensive data and rigorous methodology, not opinions or market sentiment.',
            },
            {
              icon: <Users className="w-8 h-8 active-text" />,
              title: 'Accessibility',
              description:
                'We make sophisticated financial analysis accessible to investors at all levels of experience.',
            },
            {
              icon: <BookOpen className="w-8 h-8 active-text" />,
              title: 'Education',
              description:
                'We empower investors through education, helping them understand not just what to invest in, but why.',
            },
            {
              icon: <Code className="w-8 h-8 active-text" />,
              title: 'Innovation',
              description:
                'We constantly evolve our tools and methodologies to incorporate the latest in financial theory and technology.',
            },
            {
              icon: <LightbulbIcon className="w-8 h-8 active-text" />,
              title: 'Long-Term Thinking',
              description:
                'We focus on sustainable, long-term investment strategies rather than short-term market fluctuations.',
            }].map((value, index) => (
              <div key={index} className="bg-theme-primary p-6 rounded-lg shadow-md">
                <div className="mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold text-theme-primary mb-2">
                  {value.title}
                </h3>
                <p className="text-theme-secondary">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Our Technology */}
      <section
        className={`py-16 ${
          theme === 'dark'
            ? 'bg-gradient-to-br from-gray-900 to-blue-900 text-theme-primary'
            : 'bg-gradient-to-br from-gray-100 to-yellow-50 text-gray-900'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-8">Our Technology</h2>
            <p className="text-xl opacity-80 mb-8">
              Stock Fortress combines cutting-edge technology with robust
              financial methodologies to deliver accurate, actionable investment
              insights.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div
                className={`backdrop-blur-sm rounded-lg p-6 ${
                  theme === 'dark'
                    ? 'bg-theme-secondary/10'
                    : 'bg-theme-tertiary/40'
                }`}
              >
                <BarChart2 className="w-10 h-10 active-text mb-4" />
                <h3 className="text-xl font-bold mb-2">
                  Advanced Data Processing
                </h3>
                <p className="opacity-80">
                  Our platform processes financial data from over 30,000 public
                  companies worldwide, updating in real-time to ensure you have
                  the most current information for your investment decisions.
                </p>
              </div>
              <div
                className={`backdrop-blur-sm rounded-lg p-6 ${
                  theme === 'dark'
                    ? 'bg-theme-secondary/10'
                    : 'bg-theme-tertiary/40'
                }`}
              >
                <Target className="w-10 h-10 active-text mb-4" />
                <h3 className="text-xl font-bold mb-2">Predictive Analytics</h3>
                <p className="opacity-80">
                  Our machine learning algorithms analyze historical patterns
                  and current trends to generate accurate forecasts for revenue
                  growth, profit margins, and other key financial metrics.
                </p>
              </div>
              <div
                className={`backdrop-blur-sm rounded-lg p-6 ${
                  theme === 'dark'
                    ? 'bg-theme-secondary/10'
                    : 'bg-theme-tertiary/40'
                }`}
              >
                <Globe className="w-10 h-10 active-text mb-4" />
                <h3 className="text-xl font-bold mb-2">
                  Comprehensive Data Sources
                </h3>
                <p className="opacity-80">
                  We integrate data from SEC filings, earnings reports, economic
                  indicators, and alternative data sources to provide a complete
                  picture of each investment opportunity.
                </p>
              </div>
              <div
                className={`backdrop-blur-sm rounded-lg p-6 ${
                  theme === 'dark'
                    ? 'bg-theme-secondary/10'
                    : 'bg-theme-tertiary/40'
                }`}
              >
                <Heart className="w-10 h-10 active-text mb-4" />
                <h3 className="text-xl font-bold mb-2">User-Centric Design</h3>
                <p className="opacity-80">
                  Our interface is designed to make complex financial data
                  accessible and actionable, whether you're a seasoned
                  professional or just starting your investment journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Contact */}
      <section className="py-16 bg-theme-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-theme-primary mb-8">Contact Us</h2>
            <div className="flex flex-wrap justify-center items-center gap-8">
              <div className="flex flex-col items-center">
                <Mail className="w-5 h-5 active-text mb-1" />
                <h3 className="text-sm font-semibold text-theme-primary mb-0.5">Email</h3>
                <a href="mailto:support@stockfortress.com" className="text-xs text-theme-primary hover:underline">
                  support@stockfortress.com
                </a>
              </div>
              <div className="flex flex-col items-center">
                <Twitter className="w-5 h-5 active-text mb-1" />
                <h3 className="text-sm font-semibold text-theme-primary mb-0.5">X (Twitter)</h3>
                <a href="https://x.com/stockfortress" target="_blank" rel="noopener noreferrer" className="text-xs text-theme-primary hover:underline">
                  x.com/stockfortress
                </a>
              </div>
              <div className="flex flex-col items-center">
                <Facebook className="w-5 h-5 active-text mb-1" />
                <h3 className="text-sm font-semibold text-theme-primary mb-0.5">Facebook</h3>
                <a href="https://facebook.com/stockfortress" target="_blank" rel="noopener noreferrer" className="text-xs text-theme-primary hover:underline">
                  facebook.com/stockfortress
                </a>
              </div>
              <div className="flex flex-col items-center">
                <MessageCircle className="w-5 h-5 active-text mb-1" />
                <h3 className="text-sm font-semibold text-theme-primary mb-0.5">Discord</h3>
                <a href="https://discord.gg/4m4xTnJp" target="_blank" rel="noopener noreferrer" className="text-xs text-theme-primary hover:underline">
                  discord.gg/4m4xTnJp
                </a>
              </div>
              <div className="flex flex-col items-center">
                <Instagram className="w-5 h-5 active-text mb-1" />
                <h3 className="text-sm font-semibold text-theme-primary mb-0.5">Instagram</h3>
                <a href="https://www.instagram.com/stockfortress?igsh=Y3l0c2tzaW1iMnl3&utm_source=qr" target="_blank" rel="noopener noreferrer" className="text-xs text-theme-primary hover:underline">
                  instagram.com/stockfortress
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}